import React from 'react';
import { Box, Button, TextField, SxProps, Theme, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';

interface BookingFormPromoInputProps {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onApply: () => void;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  sx?: SxProps<Theme>;
}

const BookingFormPromoInput: React.FC<BookingFormPromoInputProps> = ({ value, onChange, onApply, disabled, error, helperText, sx = {} }) => {
  const { t } = useTranslation('common');

  return (
    <Box sx={{ ...sx }}>
      {/* Input Row */}
      <StyleWrap hasError={Boolean(error)}>
        <StyledInput
          variant="filled"
          value={value}
          onChange={onChange}
          disabled={disabled}
          error={error}
          placeholder={t('common:promotionCode') ?? ''}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              onApply();
            }
          }}
          onBlur={onApply}
        />
        {disabled ? null : (
          <Button
            onClick={onApply}
            disabled={disabled}
            sx={(theme) => ({
              height: 40,
              borderRadius: 2,
              background: theme.palette.blue.main,
              fontWeight: 600,
              color: theme.palette.white.main,
              px: 2,
              boxShadow: 'none',
              whiteSpace: 'nowrap',
              flexShrink: 0,
              '&:hover': {
                background: theme.palette.blue.main + 'CC',
              },
            })}
          >
            {t('apply')}
          </Button>
        )}
      </StyleWrap>

      {/* Helper Text */}
      {helperText && (
        <Typography
          variant="caption"
          sx={(theme) => ({
            color: error ? theme.palette.error.main : theme.palette.success.main,
            mt: 0.5,
            display: 'block',
          })}
        >
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

export default BookingFormPromoInput;

const StyleWrap = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'hasError',
})<{ hasError?: boolean }>(({ theme, hasError }) => ({
  width: '100%',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  borderBottom: '1px solid',
  borderColor: hasError ? theme.palette.error.main : '#c4c4c4',
  paddingBottom: theme.spacing(0.5),
}));

const StyledInput = styled(TextField)(({ theme }) => ({
  flex: 1,
  '& .MuiFilledInput-root': {
    height: 40,
    fontWeight: 700,
    fontSize: 16,
    backgroundColor: 'transparent',
    boxShadow: 'unset',
    padding: 0,
    '&:before': {
      borderBottom: 'unset',
    },
    '&:after': {
      borderBottom: 'unset',
    },
    '&:hover': {
      backgroundColor: 'transparent',
      '&:before': {
        borderBottom: 'unset!important',
      },
    },
    '&.Mui-focused': {
      backgroundColor: 'transparent',
    },
    '&.Mui-disabled': {
      backgroundColor: 'transparent',
    },
    input: {
      height: '100%',
      padding: '0px !important',
      fontWeight: 700,
      fontSize: 16,
      color: theme.palette.black.main,
      '&::placeholder': {
        fontWeight: 400,
        fontSize: 14,
        color: theme.palette.text.secondary,
        opacity: 1,
      },
    },
  },
  '& .MuiInputBase-input.Mui-disabled': {
    WebkitTextFillColor: theme.palette.black.main,
  },
}));